import { useState } from "react";
import { fmtAed } from "./KpiCard";
import SubtypeTable from "./SubtypeTable";
import InfoTip from "./InfoTip";
import type { TrackSummaryRow, GroupBreakdownRow, FrameRecords, DisplayConfig } from "../types/api";

interface Props {
  track: "Premium" | "Commission";
  summary: TrackSummaryRow[];
  breakdown: GroupBreakdownRow[];
  tolerance: number;
  dfCri: FrameRecords;
  dfInsurer: FrameRecords;
  displayConfig?: DisplayConfig;
  insurerName?: string;  // scopes pinned columns in the drill-down tables
  // The transaction rows arrive separately from the figures (see ResultPage). The
  // amounts and counts on this panel are engine scalars and are final immediately; only
  // the per-subtype DRILL-DOWN reads rows, so only it waits.
  framesReady?: boolean;
  // AR lines read Insurer minus CRI and Group 2 is deducted (engine, finance 2026-11-02).
  isAr?: boolean;
}

const AR_G2_TIP = " On AR this line is deducted: Variance = Group 1 − Group 2 + Group 3 + Matched.";

// The four reconciliation groups in dashboard order. Labels match _render_track_column
// EXACTLY (finance relies on these names; any deviation confuses the team).
// `optional` sections are rendered only when the engine emitted that line, so an
// insurer with no such population sees no empty panel.
const GROUPS: { line: string; header: string; sub: string; tip: string; optional?: boolean }[] = [
  {
    line: "Group 1 - In Insurer, not in CRI",
    header: "GROUP 1 — In Insurer, not in CRI",
    sub: "Items the insurer has posted; CRI hasn't yet",
    tip: "The insurer has booked these; your CRI ledger has not. Usually a timing gap or something you still need to post.",
  },
  {
    line: "Group 2 - In CRI, not in Insurer",
    header: "GROUP 2 — In CRI, not in Insurer",
    sub: "CRI has posted; Insurer hasn't — investigate",
    tip: "Your CRI ledger has these; the insurer hasn't booked them yet. Usually postings the insurer still needs to make — investigate the large ones.",
  },
  {
    line: "Group 3 - Figure / Balance Mismatch",
    header: "GROUP 3 — Figure / Balance Mismatch",
    sub: "Same doc on both sides, OS balance differs",
    tip: "The same document exists on both sides but the amounts differ. Usually a figure or rounding discrepancy.",
  },
  {
    line: "Cleared in Subsequent Month",
    header: "Cleared in Subsequent Month",
    sub: "Open at period end, cleared by a later transaction",
    tip: "These were still open on the period end date, but a transaction dated after it clears them. They are off Group 1 / Group 2 so the open position is not overstated; the clearing row is outside the period and affects no balance.",
    optional: true,
  },
  {
    line: "Matched", header: "Matched", sub: "",
    tip: "Same document, same amount within tolerance, on both sides. Explained items — the pairs cancel out, and any tiny within-tolerance residual is excluded from the Difference.",
  },
];

// One complete track panel (Premium | Commission). Mirrors app/tables.py::
// _render_track_column: colored band, CRI Closing + Unreconciled Variance metrics,
// the Insurer SOA Closing Balance bar, then the four groups with subtype drill-down.
export default function TrackColumn({ track, summary, breakdown, tolerance, dfCri, dfInsurer, displayConfig, insurerName = "", framesReady = true, isAr = false }: Props) {
  const line = (name: string) => summary.find((r) => r.Line === name);
  const criClosing = line("CRI Closing")?.["Amount (AED)"] ?? 0;
  const insClosing = line("Insurer SOA Closing Balance")?.["Amount (AED)"] ?? 0;
  const variance = line("Unreconciled Variance")?.["Amount (AED)"] ?? 0;
  const reconciled = Math.abs(variance) <= tolerance;

  return (
    <div className="col card track-card">
      {/* Coloured track header band. The two tracks' colours are token pairs selected
          by data-track, so the palette stays in the stylesheet with everything else. */}
      <div className="track-band" data-track={track}>
        <span className="track-band-title">{track} Track</span>
        <InfoTip text="Premium and Commission are reconciled separately because they net independently. Each track has its own four groups and its own variance." />
      </div>

      {/* Key metrics */}
      <div className="row track-metrics">
        <div className="col kpi-card">
          <div className="kpi-label">CRI Closing</div>
          <div className="kpi-value">{fmtAed(criClosing)}</div>
        </div>
        <div className="col kpi-card">
          <div className="kpi-label">
            Unreconciled Variance
            <InfoTip text={isAr
              ? "Insurer closing minus CRI closing — both statements book the receivable with the same sign, so a tied track nets to zero."
              : "CRI closing plus Insurer closing for this track — the two ledgers carry opposite signs, so a tied track nets to zero."} />
          </div>
          <div className={`kpi-value ${reconciled ? "figure-ok" : "figure-bad"}`}>
            {fmtAed(variance)}
          </div>
        </div>
      </div>

      {/* Insurer SOA closing bar */}
      <div className="soa-bar">
        <span className="soa-bar-label">Insurer SOA Closing Balance</span>
        <strong className="soa-bar-value">{fmtAed(insClosing)}</strong>
      </div>

      {/* Four groups */}
      {GROUPS.filter((g) => !g.optional || line(g.line) !== undefined).map((g) => (
        <GroupSection key={g.line} row={line(g.line)}
          group={isAr && g.line.startsWith("Group 2") ? { ...g, tip: g.tip + AR_G2_TIP } : g}
          subtypes={breakdown.filter((b) => b.Track === track && b.Group === g.line)}
          dfCri={dfCri} dfInsurer={dfInsurer} displayConfig={displayConfig} insurerName={insurerName}
          framesReady={framesReady} />
      ))}
    </div>
  );
}

function GroupSection({ group, row, subtypes, dfCri, dfInsurer, displayConfig, insurerName = "", framesReady = true }: {
  group: { line: string; header: string; sub: string; tip: string };
  row?: TrackSummaryRow;
  subtypes: GroupBreakdownRow[];
  dfCri: FrameRecords;
  dfInsurer: FrameRecords;
  displayConfig?: DisplayConfig;
  insurerName?: string;
  framesReady?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Per-subtype expansion (multiple can be open — mirrors the old nested expanders).
  const [openSubs, setOpenSubs] = useState<Set<number>>(new Set());
  const total = row?.["Amount (AED)"] ?? 0;
  const count = typeof row?.Count === "number" ? row.Count : 0;
  const empty = count === 0;
  // Groups 1-3 with items are the work queue; Matched (and anything empty) is already
  // done. Weighting them differently is the point — they used to render identically,
  // so "investigate these" looked exactly like "nothing to do here". Labels, order and
  // tooltips are untouched: finance relies on those names.
  // Cleared-in-subsequent items are explained, like Matched: they need no chasing.
  const actionable = !empty && group.line !== "Matched"
    && group.line !== "Cleared in Subsequent Month";

  const toggleSub = (i: number) =>
    setOpenSubs((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });

  return (
    <div className={`group-row ${actionable ? "group-row--actionable" : ""} ${empty ? "group-row--empty" : ""}`}>
      {/* The InfoTip sits BESIDE the button, not inside it: a tooltip trigger nested in
          a button is not keyboard reachable (the same fix ColumnFilterGrid already
          made). The wrapper keeps them on one line. */}
      <div className="group-head-row">
        <button
          type="button"
          className="group-head"
          disabled={empty}
          aria-expanded={empty ? undefined : open}
          onClick={() => !empty && setOpen(!open)}
        >
          <span className="group-name">
            {!empty && <span className="group-caret" aria-hidden="true">{open ? "▾" : "▸"}</span>}
            {group.header}
          </span>
          {group.sub && <span className="muted group-sub">{group.sub}</span>}
        </button>
        {/* Beside the title, still outside the button: a tooltip trigger nested in a
            button is not keyboard reachable (the fix ColumnFilterGrid already made). */}
        <InfoTip text={group.tip} />
        <div className="group-figures">
          <div className="group-amount">{fmtAed(total)}</div>
          <div className="group-count">{count} item{count === 1 ? "" : "s"}</div>
        </div>
      </div>
      {open && subtypes.length > 0 && (
        <div className="group-subs">
          {subtypes.map((s, i) => {
            // The row COUNT and AMOUNT beside it are engine figures and are already
            // correct; only expanding to the rows themselves needs the frames.
            const canDrill = (s.Indices?.length ?? 0) > 0 && framesReady;
            const subOpen = openSubs.has(i);
            return (
              <div key={i} className="group-sub-item">
                <button
                  type="button"
                  className="group-subrow"
                  disabled={!canDrill}
                  aria-expanded={canDrill ? subOpen : undefined}
                  title={!framesReady && (s.Indices?.length ?? 0) > 0
                    ? "Loading transactions…" : undefined}
                  onClick={() => canDrill && toggleSub(i)}
                >
                  <span className="muted">
                    {canDrill && <span className="group-caret" aria-hidden="true">{subOpen ? "▾" : "▸"}</span>}
                    {s.Side} · {s.Subtype} ({s.Count})
                  </span>
                  <span className="soa-bar-value">{fmtAed(s["Amount (AED)"])}</span>
                </button>
                {subOpen && canDrill && (
                  <SubtypeTable
                    frame={s.Side === "CRI" ? dfCri : dfInsurer}
                    config={s.Side === "CRI" ? displayConfig?.cri : displayConfig?.insurer}
                    indices={s.Indices!}
                    pinSide={s.Side === "CRI" ? "cri" : "insurer"}
                    insurerName={insurerName}
                    title={`${s.Side} - ${s.Subtype ?? "Transactions"}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
